import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  Memory as AssetsIcon,
  EmojiEvents as SolutionsIcon,
  Timer as SolveIcon,
} from '@mui/icons-material';

import {
  getTenantId,
  listRuns,
  deleteRun,
  RunSummary,
  strategyName,
} from '../../services/amsoApi';

const STATUS_CONFIG: Record<
  string,
  { color: 'success' | 'error' | 'warning' | 'info' | 'default'; label: string }
> = {
  completed: { color: 'success', label: 'Completed' },
  failed: { color: 'error', label: 'Failed' },
  running: { color: 'warning', label: 'Running' },
};

function formatTime(v?: number | null): string {
  if (v == null) return '-';
  return v < 1 ? `${(v * 1000).toFixed(0)} ms` : `${v.toFixed(1)} s`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }) +
      ' at ' +
      d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    );
  } catch {
    return iso;
  }
}

function StatChip({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
}) {
  return (
    <Tooltip title={label}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ color: 'text.disabled', display: 'flex', fontSize: '1rem' }}>
          {icon}
        </Box>
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function RunCard({
  run,
  onClick,
  onDelete,
}: {
  run: RunSummary;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const cfg = STATUS_CONFIG[run.status] || {
    color: 'default' as const,
    label: run.status,
  };

  return (
    <Card
      variant="outlined"
      sx={{
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': { boxShadow: 3, borderColor: 'primary.light' },
        position: 'relative',
      }}>
      <CardActionArea onClick={onClick} sx={{ p: 0 }}>
        <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              mb: 1.5,
              pr: 4,
            }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {run.name || run.run_id}
              </Typography>
              {run.name && (
                <Typography variant="caption" color="text.disabled" noWrap>
                  {run.run_id}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: 'block' }}>
                {strategyName(run.tier)}
              </Typography>
            </Box>
            <Chip
              label={cfg.label}
              size="small"
              color={cfg.color}
              variant="outlined"
              sx={{ fontWeight: 500, flexShrink: 0 }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              flexWrap: 'wrap',
            }}>
            <StatChip
              icon={<AssetsIcon fontSize="small" />}
              value={run.total_assets?.toLocaleString() ?? '-'}
              label="Assets"
            />
            <StatChip
              icon={<SolutionsIcon fontSize="small" />}
              value={run.n_solutions ?? '-'}
              label="Solutions"
            />
            <StatChip
              icon={<SolveIcon fontSize="small" />}
              value={formatTime(run.solve_time)}
              label="Solve time"
            />
            <StatChip
              icon={<TimeIcon fontSize="small" />}
              value={formatDate(run.created_at)}
              label="Created"
            />
          </Box>
        </CardContent>
      </CardActionArea>

      <Tooltip title="Delete run">
        <IconButton
          size="small"
          onClick={onDelete}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            opacity: 0.3,
            '&:hover': { opacity: 1, color: 'error.main' },
          }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Card>
  );
}

function RunListPage() {
  const history = useHistory();
  const tenantId = getTenantId();
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RunSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listRuns(tenantId || undefined);
      setRuns(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    const hasRunning = runs.some(r => r.status === 'running');
    if (!hasRunning) return;
    const interval = setInterval(fetchRuns, 10_000);
    return () => clearInterval(interval);
  }, [runs, fetchRuns]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRun(deleteTarget.run_id);
      setRuns(prev => prev.filter(r => r.run_id !== deleteTarget.run_id));
    } catch (err: any) {
      setError(`Failed to delete: ${err.message}`);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Optimization Runs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {runs.length} run{runs.length !== 1 ? 's' : ''}
            {loading && <CircularProgress size={12} sx={{ ml: 1 }} />}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchRuns}
            disabled={loading}
            size="small">
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => history.push('/amso/new')}
            disableElevation>
            New Run
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && runs.length === 0 && (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 6, px: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No optimization runs yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
            Create your first run to start optimizing asset management
            strategies.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => history.push('/amso/new')}
            disableElevation>
            New Run
          </Button>
        </Card>
      )}

      <Grid container spacing={2}>
        {runs.map(run => (
          <Grid item xs={12} key={run.run_id}>
            <RunCard
              run={run}
              onClick={() => history.push(`/amso/runs/${run.run_id}`)}
              onDelete={e => {
                e.stopPropagation();
                setDeleteTarget(run);
              }}
            />
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete run?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete run{' '}
            <strong>{deleteTarget?.name || deleteTarget?.run_id}</strong> and
            all its solutions, artifacts, and analysis data. This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            disableElevation>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RunListPage;
